import {BeneficiaryPerson} from 'Beneficiaries/models/BeneficiaryPerson';
import {ContingentBeneficiary, PrimaryBeneficiary} from 'Beneficiaries/models/PersonTypes';
import {Async, Empty, Loaded} from 'Core/models/Async';
import {createAction} from 'Shared/utilities/actionCreator';

export enum BeneficiaryFormActionTypes {
  UPDATE_FIELD = 'beneficiaries/beneficiaryForm/UPDATE_FIELD',
  FEETCH_BENEFICIARY_DETAIL = 'beneficiaries/beneficiaryForm/FEETCH_BENEFICIARY_DETAIL',
  CHANGE_BENEFICIARY_PERSON = 'beneficiaries/beneficiaryForm/CHANGE_BENEFICIARY_PERSON',
  CHANGE_POLICY_BENEFICIARY_PERSONS = 'beneficiaries/beneficiaryForm/CHANGE_POLICY_BENEFICIARY_PERSONS',
  CLEAR_BENEFICIARY_FORM_STATE = 'beneficiaries/beneficiaryForm/CLEAR_BENEFICIARY_FORM_STATE',
  VALIDATE_AND_SUBMIT_BENEFICIARY_FORM = 'beneficiaries/beneficiaryForm/VALIDATE_AND_SUBMIT_BENEFICIARY_FORM',
  CHANGE_BENEFICIARY_PERCENTAGE = 'beneficiaries/beneficiaryForm/CHANGE_BENEFICIARY_PERCENTAGE',
  VALIDATE_BENEFICIARY_PERCENTAGE = 'beneficiaries/beneficiaryForm/VALIDATE_BENEFICIARY_PERCENTAGE',
  VALIDATE_AND_SAVE_BENEFICIARY_PERCENTAGES = 'beneficiaries/beneficiaryForm/VALIDATE_AND_SAVE_BENEFICIARY_PERCENTAGES',
}

export const updateField = (field: {[key in keyof BeneficiaryPerson]: BeneficiaryPerson[key]}) =>
  createAction(BeneficiaryFormActionTypes.UPDATE_FIELD, {field});
export const changeBeneficiaryPerson = (beneficiaryPerson: Async<BeneficiaryPerson>) =>
  createAction(BeneficiaryFormActionTypes.CHANGE_BENEFICIARY_PERSON, {beneficiaryPerson});
export const changePolicyBeneficiaryPersons = (beneficiaryPersons: Async<BeneficiaryPerson[]>) =>
  createAction(BeneficiaryFormActionTypes.CHANGE_POLICY_BENEFICIARY_PERSONS, {beneficiaryPersons});
export const validateAndSubmitForm = (beneficiaryPerson: BeneficiaryPerson, policyNumber: string) =>
  createAction(BeneficiaryFormActionTypes.VALIDATE_AND_SUBMIT_BENEFICIARY_FORM, {
    beneficiaryPerson,
    policyNumber,
  });
export const changeBeneficiaryPercent = (value: number, id?: string) =>
  createAction(BeneficiaryFormActionTypes.CHANGE_BENEFICIARY_PERCENTAGE, {id, value});
export const validateBeneficiaryPercent = () =>
  createAction(BeneficiaryFormActionTypes.VALIDATE_BENEFICIARY_PERCENTAGE);
export const validateAndSaveBeneficiaryPercents = (
  policyNumber: string,
  beneficiaryPersons: BeneficiaryPerson[],
  id: string
) =>
  createAction(BeneficiaryFormActionTypes.VALIDATE_AND_SAVE_BENEFICIARY_PERCENTAGES, {
    policyNumber,
    beneficiaryPersons: beneficiaryPersons.filter(
      bp => bp.personType?.id === PrimaryBeneficiary.id || bp.personType?.id === ContingentBeneficiary.id
    ),
    id,
  });
export const clearState = () => createAction(BeneficiaryFormActionTypes.CLEAR_BENEFICIARY_FORM_STATE);

type BeneficiaryFormActions =
  | ReturnType<typeof updateField>
  | ReturnType<typeof changeBeneficiaryPerson>
  | ReturnType<typeof changePolicyBeneficiaryPersons>
  | ReturnType<typeof clearState>
  | ReturnType<typeof changeBeneficiaryPercent>
  | ReturnType<typeof validateBeneficiaryPercent>;

type BeneficiaryFormState = {
  beneficiaryPerson: Async<BeneficiaryPerson>;
  originalBeneficiaryPerson: Async<BeneficiaryPerson>;
  policyBeneficiaryPersons: Async<BeneficiaryPerson[]>;
  originalPolicyBeneficiaryPersons: Async<BeneficiaryPerson[]>;
  primaryBeneficiaryPercentError: boolean;
  contingentBeneficiaryPercentError: boolean;
};

export const DEFAULT_STATE = {
  beneficiaryPerson: new Empty(),
  originalBeneficiaryPerson: new Empty(),
  policyBeneficiaryPersons: new Empty(),
  originalPolicyBeneficiaryPersons: new Empty(),
  primaryBeneficiaryPercentError: false,
  contingentBeneficiaryPercentError: false,
};

export default (state: BeneficiaryFormState = DEFAULT_STATE, action: BeneficiaryFormActions): BeneficiaryFormState => {
  switch (action.type) {
    case BeneficiaryFormActionTypes.UPDATE_FIELD:
      return {
        ...state,
        beneficiaryPerson: new Loaded({
          ...(state.beneficiaryPerson as Loaded<BeneficiaryPerson>).Data,
          ...action.payload.field,
        }),
      };

    case BeneficiaryFormActionTypes.CHANGE_BENEFICIARY_PERSON:
      return {
        ...state,
        beneficiaryPerson: action.payload.beneficiaryPerson,
        originalBeneficiaryPerson: new Loaded({
          ...(action.payload.beneficiaryPerson as Loaded<BeneficiaryPerson>).Data,
        }),
      };

    case BeneficiaryFormActionTypes.CHANGE_POLICY_BENEFICIARY_PERSONS:
      return {
        ...state,
        policyBeneficiaryPersons: action.payload.beneficiaryPersons,
        originalPolicyBeneficiaryPersons: new Loaded(
          (action.payload.beneficiaryPersons as Loaded<BeneficiaryPerson[]>).Data.map(data => ({...data}))
        ),
      };

    case BeneficiaryFormActionTypes.CHANGE_BENEFICIARY_PERCENTAGE:
      const policyBeneficiaryPersons = new Loaded(
        (state.policyBeneficiaryPersons as Loaded<BeneficiaryPerson[]>).Data.map(pb => {
          if (pb.id === action.payload.id) {
            pb.beneficiaryPercent = action.payload.value;
          }

          return pb;
        })
      );

      return {
        ...state,
        policyBeneficiaryPersons,
      };

    case BeneficiaryFormActionTypes.VALIDATE_BENEFICIARY_PERCENTAGE:
      const primaryBeneficiaries = (state.policyBeneficiaryPersons as Loaded<BeneficiaryPerson[]>).Data.filter(
        bp => bp.personType?.id === PrimaryBeneficiary.id
      );
      const contingentBeneficiaries = (state.policyBeneficiaryPersons as Loaded<BeneficiaryPerson[]>).Data.filter(
        bp => bp.personType?.id === ContingentBeneficiary.id
      );
      const isEmptyOrEqualTo100 = (beneficiaries: BeneficiaryPerson[]) =>
        beneficiaries.length > 0 &&
        (beneficiaries
          .map(pb => pb.beneficiaryPercent)
          .filter(Boolean)
          .reduce((a, b) => a! + b!, 0) || 0) !== 100;

      return {
        ...state,
        primaryBeneficiaryPercentError: isEmptyOrEqualTo100(primaryBeneficiaries),
        contingentBeneficiaryPercentError: isEmptyOrEqualTo100(contingentBeneficiaries),
      };

    case BeneficiaryFormActionTypes.CLEAR_BENEFICIARY_FORM_STATE:
      return DEFAULT_STATE;

    default:
      return state;
  }
};
